import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  Avatar,
  Typography,
  Tooltip,
  Menu,
  MenuItem,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import { useLocation } from "react-router-dom";
import ClusterDenIcon from "../../Images/Dashboard/ClusterDenIcon.png";
import { useTheme } from "@mui/material/styles";

import LogoutIcon from "@mui/icons-material/Logout";
import ShareRoundedIcon from "@mui/icons-material/ShareRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import WorkspacesOutlinedIcon from "@mui/icons-material/WorkspacesOutlined";
import CloseIcon from "@mui/icons-material/Close";
import AiFaceIcon from "./AiFaceIcon";
// Confirmed via respond.io's own production bundle (icon component + naming
// convention match — profile-2user, search-normal, etc.) that this is the
// icon set they use; standardizing the main nav rail on it too instead of
// picking individual MUI icons ad hoc. Only the 12 nav-rail items below use
// these — everything else (profile popover, Onboard preview) is untouched,
// out of scope for this pass.
import {
  Category2 as DashboardIconIx,
  DirectInbox as InboxIconIx,
  Profile2User as ContactsIconIx,
  Radio as BroadcastIconIx,
  TaskSquare as TaskIconIx,
  Hierarchy as WorkflowIconIx,
  DocumentText as TemplatesIconIx,
  Chart as ReportsIconIx,
  Call as CallIconIx,
  Setting2 as SettingsIconIx,
  // No literal rocket icon in this set — paper-plane is the closest
  // "launch/get started" substitute. Flagged for Rahim to confirm.
  Send2 as OnboardIconIx,
  Moon as DarkModeIconIx,
  Sun1 as LightModeIconIx,
  MessageQuestion as HelpIconIx,
} from "iconsax-react";
import { useSelector, useDispatch } from "react-redux";
import {
  setSelectedIndex,
  setCurrentPage,
  setBreadcrumbs,
} from "../../redux/Slice/Dashboard/leftNavSlice";
import { fetchUserDetails, fetchUserPermissions } from "../../redux/Slice/User/userDetailsSlice";
import { logoutUser } from "../../redux/Slice/Auth/authSlice";
import { useThemeContext } from "../../contexts/ThemeContext";
import { fetchOnboardStep } from "../../services/accountApi";
import { getAccessToken } from "../../utils/tokenManager";
import { getAuthToken } from "../../utils/getAuthToken";
import WorkspaceSwitcher from "../Workspace/WorkspaceSwitcher";
import {
  RAIL_POPUP_TEXT,
  RAIL_POPUP_SUBTEXT,
  RAIL_POPUP_DIVIDER,
  railPopupItemSx,
  railPopoverPaperSx,
  RAIL_POPUP_GAP_PX,
  getRailPopoverOffsetPx,
} from "./railPopoverStyles";

// Below this viewport height the rail's icon list no longer fits without scrolling
// (width-based MUI breakpoints don't account for short/small desktop windows) —
// shrink box sizes, icon sizes, and gaps instead of letting it scroll.
const COMPACT_HEIGHT_MQ = "@media (max-height:900px)";

const railMixin = (theme, width) => ({
  width,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.easeInOut,
    duration: 600,
  }),
  overflowX: "hidden",
  backgroundColor:
    theme.palette.mode === "dark"
      ? theme.customColors.dark.navigation
      : theme.palette.background.default,
});

// Left nav is a permanent icon-only rail — it never opens/expands.
const Drawer = styled(MuiDrawer)(({ theme, width }) => ({
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  maxWidth: "100%", // Ensure drawer doesn't exceed parent width
  ...railMixin(theme, width),
  "& .MuiDrawer-paper": {
    ...railMixin(theme, width),
    overflow: "visible",
    maxWidth: "100%",
    top: "var(--trial-banner-height, 0px)",
    height: "calc(100vh - var(--trial-banner-height, 0px))",
  },
}));

// Compact mirror of the 3 onboarding steps shown on the Onboard page — kept as its own
// small list here rather than importing the page's full data (which also carries
// navigation handlers this hover preview doesn't need). Keep copy in sync with
// pages/Onboard/Onboard.jsx's `onboards` array if it changes.
const ONBOARD_PREVIEW_STEPS = [
  { title: "Connect a channel", StepIcon: ShareRoundedIcon, color: "#0EA5E9" },
  { title: "Add your team", StepIcon: GroupsRoundedIcon, color: "#0E9046" },
  { title: "Set up automation", StepIcon: AccountTreeRoundedIcon, color: "#8B5CF6" },
];

// Tooltip for the collapsed rail's icons — deliberately inverted from the app's own
// light/dark mode so it always contrasts against the nav: light bubble in dark mode
// (matching Rahim's reference design), dark bubble in light mode (conventional tooltip
// look, since the light-mode nav background is already light).
const RAIL_TOOLTIP_SX = {
  tooltip: {
    sx: {
      backgroundColor: (theme) => (theme.palette.mode === "dark" ? "#E2E2E4" : "#1A1A1A"),
      color: (theme) => (theme.palette.mode === "dark" ? "#1A1A1A" : "#F5F5F5"),
      fontSize: "0.8rem",
      fontWeight: 600,
      borderRadius: "6px",
      px: 1.5,
      py: 0.75,
      boxShadow: (theme) =>
        theme.palette.mode === "dark" ? "0 6px 16px rgba(0,0,0,0.35)" : "0 4px 12px rgba(0,0,0,0.15)",
    },
  },
  arrow: {
    sx: {
      color: (theme) => (theme.palette.mode === "dark" ? "#E2E2E4" : "#1A1A1A"),
      boxShadow: "none",
      filter: "none",
      marginLeft: "-7px !important",
    },
  },
  popper: {
    modifiers: [{ name: "offset", options: { offset: [0, 3] } }],
  },
};

// Rich hover-preview card for the collapsed sidebar's Onboard icon — mirrors the
// real account-level progress the Onboard page itself fetches (`fetchOnboardStep`,
// same 0-3 value), passed down as a prop so it's fetched once by the parent
// LeftNav instead of refetching on every hover-open. Was hardcoded 0/3 for a
// while (2026-07-14 build); wired to the real value 2026-08-18 once it was
// noticed the two never matched (page showed 3/3 while this stayed stuck at 0/3).
function OnboardProgressPreview({ onboardStep }) {
  const stepCompletion = ONBOARD_PREVIEW_STEPS.map((_, index) => onboardStep > index);
  const completedCount = stepCompletion.filter(Boolean).length;
  const progressPercent = Math.round((completedCount / ONBOARD_PREVIEW_STEPS.length) * 100);

  return (
    <Box sx={{ width: 288, py: 1.75 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.25, pb: 1.75 }}>
        <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", color: "text.primary" }}>
          Your progress
        </Typography>
        <Typography sx={{ fontSize: "0.78rem", color: "text.secondary" }}>
          {completedCount}/{ONBOARD_PREVIEW_STEPS.length}
        </Typography>
      </Box>
      <Box sx={{ mx: 2.25, mb: 1.75, height: 6, borderRadius: 999, backgroundColor: "rgba(127,127,127,0.25)", overflow: "hidden" }}>
        <Box sx={{ width: `${progressPercent}%`, height: "100%", backgroundColor: "primary.main", transition: "width 0.3s ease" }} />
      </Box>
      {ONBOARD_PREVIEW_STEPS.map((step, index) => {
        const isCompleted = stepCompletion[index];
        // Same "unlocked once the previous step is done" rule as the Onboard
        // page's own stepCompletion/isActive logic (a completed step always
        // satisfies this too, since onboardStep > index implies onboardStep >
        // index-1 as well).
        const isActive = index === 0 || stepCompletion[index - 1];
        return (
          <Box
            key={step.title}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: 2.25,
              py: 1.15,
              borderRadius: "10px",
              mx: 1,
            }}
          >
            <Box
              sx={{
                width: 22,
                height: 22,
                borderRadius: "50%",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "11px",
                fontWeight: 700,
                backgroundColor: (theme) =>
                  isActive
                    ? theme.palette.mode === "dark"
                      ? "rgba(79,195,137,0.22)"
                      : "rgba(14,144,70,0.15)"
                    : "rgba(127,127,127,0.18)",
                color: isActive ? "primary.main" : "text.disabled",
              }}
            >
              {isCompleted ? (
                <CheckCircleRoundedIcon sx={{ fontSize: 13, color: (theme) => (theme.palette.mode === "dark" ? "#4FC389" : "#0E9046") }} />
              ) : isActive ? (
                index + 1
              ) : (
                <LockOutlinedIcon sx={{ fontSize: 11 }} />
              )}
            </Box>
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: "7px",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: (theme) =>
                  isActive ? `${step.color}${theme.palette.mode === "dark" ? "33" : "1A"}` : "rgba(127,127,127,0.14)",
              }}
            >
              <step.StepIcon sx={{ fontSize: 14, color: isActive ? step.color : "text.disabled" }} />
            </Box>
            <Typography
              sx={{
                fontSize: "0.8rem",
                fontWeight: 500,
                color: isActive ? "text.primary" : "text.secondary",
              }}
            >
              {step.title}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

const LeftNav = React.memo(() => {
  const dispatch = useDispatch();
  const selectedIndex = useSelector((state) => state.leftNav.selectedIndex);
  const { userDetails, userPermissions } = useSelector((state) => state.userDetails);
  // Available immediately after login (authSlice.js), before fetchUserDetails resolves —
  // used as a fallback so the dropdown never shows a generic placeholder for a logged-in user.
  const authUser = useSelector((state) => state.auth.user);
  const displayName = userDetails?.name || authUser?.name;
  const displayEmail = userDetails?.email || authUser?.email;
  const subscriptionFeatures = useSelector((state) => state.subscription?.features);
  // Phase 6 ("Client UI") QR gating — a single cached value already loaded once at login/
  // workspace-switch (see workspaceController.js's getWorkspaces/getWorkspace/switchWorkspace),
  // not a seventh independent "what's my connection type" endpoint call. Fails open (allowed)
  // while this hasn't loaded yet or the workspace isn't QR-connected — same fail-open idiom as
  // callsNavAllowed/aiNavAllowed above.
  const currentWorkspace = useSelector((state) => state.workspace?.currentWorkspace);
  const isQrConnectedWorkspace = currentWorkspace?.whatsappConnectionType === "baileys";

  // Real account-level onboarding progress (0-3), same value/endpoint the Onboard
  // page itself reads (see pages/Onboard/Onboard.jsx) — fetched once here so the
  // rail's hover preview (OnboardProgressPreview) always matches instead of
  // showing its old hardcoded 0/3.
  const [onboardStep, setOnboardStep] = useState(0);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let token = getAccessToken();
        if (!token) token = await getAuthToken();
        if (!token) return;
        const step = await fetchOnboardStep(token);
        if (!cancelled) setOnboardStep(step);
      } catch (err) {
        console.error("Failed to load onboarding progress for LeftNav preview:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const aiPermissionAllowed =
    userPermissions == null ||
    userPermissions?.permissions?.AI?.View === true ||
    userPermissions?.permissions?.AI?.Edit === true;
  // Fail-open while features haven't loaded yet, same as the permissions check above.
  const aiFeatureEnabled =
    subscriptionFeatures == null ||
    subscriptionFeatures.length === 0 ||
    subscriptionFeatures.find((f) => f.key === "aiAssistants")?.enabled !== false;
  const aiNavAllowed = aiPermissionAllowed && aiFeatureEnabled;
  // Same fail-open pattern — don't hide the nav item while permissions are still loading.
  const callsPermissionAllowed = userPermissions == null || userPermissions?.permissions?.Calls?.View === true;
  // Same fail-open pattern as aiFeatureEnabled above. Checking `enabled` alone isn't enough: a
  // per-customer override (admin panel's Customer Limits, for custom-plan accounts) can only
  // ever change the numeric limit, never the `enabled` flag itself (that's inherited from the
  // plan's own base template) — so an account whose admin zeroed both minute limits to disable
  // calling for them specifically would still show `enabled: true` here. Mirrors the same
  // enabled-AND-limit check calling.webhook.js's inbound gate already uses — show the nav only
  // if the feature is on AND at least one direction actually has capacity (0/0 in both
  // directions means "no calling possible at all", same as the feature being off outright).
  const callMinutesInboundFeature = subscriptionFeatures?.find((f) => f.key === "callMinutesInbound");
  const callMinutesOutboundFeature = subscriptionFeatures?.find((f) => f.key === "callMinutesOutbound");
  const callsFeatureEnabled =
    subscriptionFeatures == null ||
    subscriptionFeatures.length === 0 ||
    (callMinutesOutboundFeature?.enabled !== false &&
      (Number(callMinutesInboundFeature?.limit || 0) > 0 || Number(callMinutesOutboundFeature?.limit || 0) > 0));
  // Calling is a permanent, by-design QR limitation (Baileys can only detect/reject call
  // signaling, never carry audio/video — see the plan's non-goals) — not previously checked
  // here despite the comment below implying it already was.
  const callsNavAllowed = callsPermissionAllowed && callsFeatureEnabled && !isQrConnectedWorkspace;
  // Templates and Bulk Campaigns/Broadcast are 100% Meta-template-gated at the data-model level
  // (see the plan's Phase 6) — a QR-connected workspace can never send from either, so both nav
  // entries are hidden the same way callsNavAllowed hides Calls.
  const templatesNavAllowed = !isQrConnectedWorkspace;
  const broadcastNavAllowed = !isQrConnectedWorkspace;
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();

  // Theme toggle + profile menu — moved down from TopNav to the bottom of the rail
  const { mode, toggleTheme } = useThemeContext();
  const isDarkMode = mode === "dark";
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const isProfileMenuOpen = Boolean(profileAnchorEl);
  const [workspaceModalOpen, setWorkspaceModalOpen] = useState(false);
  const [profilePopoverOffset, setProfilePopoverOffset] = useState(RAIL_POPUP_GAP_PX);

  const handleProfileMenuClick = (event) => {
    setProfilePopoverOffset(getRailPopoverOffsetPx(event.currentTarget, drawerRef.current));
    setProfileAnchorEl(event.currentTarget);
  };
  const handleProfileMenuClose = () => setProfileAnchorEl(null);
  const handleLogout = async () => {
    await dispatch(logoutUser());
    navigate("/");
    handleProfileMenuClose();
  };

  useEffect(() => {
    if (!userDetails?._id) {
      dispatch(fetchUserDetails());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- userDetails intentionally excluded to prevent loops
  }, [dispatch]);

  useEffect(() => {
    if (userPermissions == null) {
      dispatch(fetchUserPermissions());
    }
  }, [dispatch, userPermissions]);

  const { accountid, userid, templateId } = useParams();
  const location = useLocation();

  // Organize menu items by sections


  const MenuSections = useMemo(
    () => [
      {
        heading: "GENERAL",
        items: [
          {
            text: "Onboard",
            Icon: OnboardIconIx,
            url: `/home/${accountid}/onboard`,
          },
          { text: "Dashboard", Icon: DashboardIconIx, url: `/home/${accountid}` },
          {
            text: "Inbox",
            Icon: InboxIconIx,
            // Base inbox only — do not append /chat/:id so other pages' nav targets don't preserve an open chat URL
            url: `/home/${accountid}/inbox/${
              userDetails ? userDetails._id : userid
            }`,
            matches: (path) =>
              path.startsWith(`/home/${accountid}/inbox/`),
          },
          {
            text: "Contacts",
            Icon: ContactsIconIx,
            url: `/home/${accountid}/contacts`,
          },
          ...(broadcastNavAllowed
            ? [
                {
                  text: "Broadcast",
                  Icon: BroadcastIconIx,
                  url: `/home/${accountid}/contact-groups`,
                },
              ]
            : []),
          {
            text: "Tasks",
            Icon: TaskIconIx,
            url: `/home/${accountid}/tasks`,
          },
          {
            text: "Workflow",
            Icon: WorkflowIconIx,
            url: `/home/${accountid}/workflow`,
          },
          ...(templatesNavAllowed
            ? [
                {
                  text: "Templates",
                  Icon: TemplatesIconIx,
                  url: `/home/${accountid}/templates`,
                  matches: (path) =>
                    path === `/home/${accountid}/templates` ||
                    path === `/home/${accountid}/templates/create` ||
                    path === `/home/${accountid}/templates/analytics/${templateId}`,
                },
              ]
            : []),
          {
            text: "Reports",
            Icon: ReportsIconIx,
            url: `/home/${accountid}/reports`,
          },
          ...(callsNavAllowed
            ? [
                {
                  text: "Calls",
                  Icon: CallIconIx,
                  url: `/home/${accountid}/calls`,
                },
              ]
            : []),
          ...(aiNavAllowed
            ? [
                {
                  text: "AI Agents",
                  Icon: AiFaceIcon,
                  url: `/home/${accountid}/ai`,
                  matches: (path) =>
                    path === `/home/${accountid}/ai`,
                },
              ]
            : []),
        ],
      },
      {
        heading: "SETTINGS",
        items: [
          {
            text: "Settings",
            Icon: SettingsIconIx,
            url: `/home/${accountid}/settings`,
          },
        ],
      },
    ],
    [accountid, userDetails, userid, aiNavAllowed, callsNavAllowed, templatesNavAllowed, broadcastNavAllowed, templateId]
  );

  // Flatten menu items for index tracking
  const DrawerData = useMemo(() => {
    return MenuSections.flatMap((section) => section.items);
  }, [MenuSections]);

  // Drawer's paper is position:fixed (MUI default), so it needs an explicit pixel
  // width synced from its actual in-flow container — plain "100%" resolves against
  // the viewport, not the narrow Grid column. Measured with useLayoutEffect (not
  // useEffect) so the correct width is committed before the browser paints; doing
  // this in a post-paint effect is what caused the old "opens then closes" flash.
  const [drawerWidth, setDrawerWidth] = useState(0);
  const drawerRef = useRef(null);

  useLayoutEffect(() => {
    const updateDrawerWidth = () => {
      if (drawerRef.current) {
        setDrawerWidth(drawerRef.current.offsetWidth);
      }
    };
    updateDrawerWidth();
    window.addEventListener("resize", updateDrawerWidth);
    return () => {
      window.removeEventListener("resize", updateDrawerWidth);
    };
  }, []);

  // Sync selected index / breadcrumbs to the current route
  useEffect(() => {
    // Workflow create tab route matching
    const workflowCreateRegex = new RegExp(
      `^/home/${accountid}/workflow/[^/]+/create(/.*)?$`
    );
    if (workflowCreateRegex.test(location.pathname)) {
      // Find the workflow menu item index
      const workflowIndex = DrawerData.findIndex(
        (item) => item.text === "Workflow"
      );
      if (workflowIndex !== -1) {
        dispatch(setSelectedIndex(workflowIndex));
        dispatch(setCurrentPage("Workflow"));
      }
    } else {
      // Existing logic
      const currentIndex = DrawerData.findIndex(
        (item) =>
          item.url === location.pathname ||
          (item.matches && item.matches(location.pathname))
      );
      if (currentIndex !== -1) {
        dispatch(setSelectedIndex(currentIndex));
        const selectedItem = DrawerData[currentIndex];
        dispatch(setCurrentPage(selectedItem.text));
        if (
          selectedItem.text === "Templates" &&
          location.pathname.includes("/create")
        ) {
          dispatch(setBreadcrumbs(["Templates", "Create"]));
        } else {
          dispatch(setBreadcrumbs([selectedItem.text]));
        }
        if (
          selectedItem.text === "Templates" &&
          location.pathname.includes("/analytics")
        ) {
          dispatch(setBreadcrumbs(["Templates", "Analytics"]));
        } else {
          dispatch(setBreadcrumbs([selectedItem.text]));
        }
      }
    }
  }, [dispatch, location.pathname, DrawerData, accountid, templateId]);

  const handleListItemClick = (index) => {
    dispatch(setSelectedIndex(index));
    if (index < DrawerData.length) {
      const selectedItem = DrawerData[index];

      // Update current page in redux
      dispatch(setCurrentPage(selectedItem.text));

      // Handle breadcrumbs for template pages
      if (
        selectedItem.text === "Templates" &&
        location.pathname.includes("/create")
      ) {
        dispatch(setBreadcrumbs(["Templates", "Create"]));
      } else {
        dispatch(setBreadcrumbs([selectedItem.text]));
      }
    }
  };

  return (
    <Box
      ref={drawerRef}
      sx={{
        display: "flex",
        width: "100%",
        maxWidth: "100%", // Ensure it doesn't exceed grid column width
        flexDirection: "column",
        alignItems: "center",
        overflow: { xs: "auto", sm: "auto", md: "hidden", lg: "hidden", xl: "hidden" },
        overflowX: "hidden", // Prevent horizontal scrollbar on all breakpoints
        boxSizing: "border-box", // Include padding/border in width calculation
      }}
    >
      <Drawer variant="permanent" width={drawerWidth}>
        {/* Logo */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            pt: 2,
            pb: 2,
            marginTop: {
              xs: 0,
              sm: 0,
              md: "8px", // Align with TopNav text for md
              lg: "8px", // Align with TopNav text for lg
              xl: 0,
            },
            [COMPACT_HEIGHT_MQ]: { pt: 1, pb: 1, marginTop: 0 },
          }}
        >
          <Avatar
            sx={{
              bgcolor: "transparent",
              width: { xs: 36, sm: 36, md: 32, lg: 28, xl: 40 }, // Decreased more for LG
              height: { xs: 36, sm: 36, md: 32, lg: 28, xl: 40 }, // Decreased more for LG
              [COMPACT_HEIGHT_MQ]: { width: 30, height: 30 },
            }}
          >
            <Box
              component="img"
              src={ClusterDenIcon}
              alt="ClusterDen"
              sx={{ width: "100%", height: "100%" }}
            />
          </Avatar>
        </Box>

        <Box
          sx={{
            width: "100%",
            overflowY: "auto",
            overflowX: "hidden", // Prevent horizontal scrollbar
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            // Make scrollbar thinner
            scrollbarWidth: "thin", // Firefox
            msOverflowStyle: "auto", // IE and Edge (old)
            "&::-webkit-scrollbar": {
              width: "6px", // Thin scrollbar
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: isDark ? "#555" : "#ccc",
              borderRadius: "3px",
            },
          }}
        >
          {MenuSections.map((section, sectionIndex) => (
            <React.Fragment key={`section-${sectionIndex}`}>
              <List
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: { xs: 0.35, sm: 0.35, md: 0.35, lg: 0.5, xl: 0.65 },
                  py: 0,
                  mt: sectionIndex > 0 ? { xs: 0.35, sm: 0.35, md: 0.35, lg: 0.5, xl: 0.65 } : 0,
                  [COMPACT_HEIGHT_MQ]: { gap: "3px", mt: sectionIndex > 0 ? "3px" : 0 },
                }}
              >
                {section.items.map((item, itemIndex) => {
                  // Calculate the overall index in the flattened menu
                  const globalIndex =
                    MenuSections.slice(0, sectionIndex).reduce(
                      (acc, s) => acc + s.items.length,
                      0
                    ) + itemIndex;

                  return (
                    <Link
                      key={item.text}
                      to={item.url}
                      style={{
                        textDecoration: "none",
                        color: "inherit",
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <ListItem
                        disablePadding
                        sx={{
                          width: "100%",
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        <Tooltip
                          title={item.text === "Onboard" ? <OnboardProgressPreview onboardStep={onboardStep} /> : item.text}
                          placement={item.text === "Onboard" ? "right-start" : "right"}
                          arrow={item.text !== "Onboard"}
                          componentsProps={
                            item.text === "Onboard"
                              ? {
                                  tooltip: {
                                    sx: {
                                      ...railPopoverPaperSx,
                                      p: 0,
                                      maxWidth: "none",
                                    },
                                  },
                                  popper: {
                                    modifiers: [{ name: "offset", options: { offset: [0, RAIL_POPUP_GAP_PX] } }],
                                  },
                                }
                              : RAIL_TOOLTIP_SX
                          }
                        >
                          <ListItemButton
                            onClick={() => handleListItemClick(globalIndex)}
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: (theme) =>
                                selectedIndex === globalIndex
                                  ? theme.palette.primary.main
                                  : "transparent",
                              color: (theme) =>
                                selectedIndex === globalIndex
                                  ? theme.palette.primary.contrastText
                                  : theme.palette.text.primary,
                              borderRadius: "12px",
                              padding: 0,
                              width: { xs: 46, sm: 46, md: 46, lg: 40, xl: 48 },
                              height: { xs: 46, sm: 46, md: 46, lg: 40, xl: 48 },
                              minWidth: { xs: 46, sm: 46, md: 46, lg: 40, xl: 48 },
                              [COMPACT_HEIGHT_MQ]: { width: 40, height: 40, minWidth: 40 },
                              flexGrow: 0,
                              flexShrink: 0,
                              mx: "auto",
                              transition: "none",
                              "&:hover": {
                                backgroundColor: (theme) =>
                                  selectedIndex === globalIndex
                                    ? theme.palette.primary.main
                                    : theme.palette.action.hover,
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 0,
                                mr: 0,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                color: (theme) =>
                                  selectedIndex === globalIndex
                                    ? theme.palette.primary.contrastText
                                    : theme.palette.text.primary,
                              }}
                            >
                              {typeof item.Icon === "function" ? (
                                <item.Icon
                                  sx={{
                                    width: { xs: 36, sm: 36, md: 36, lg: 28, xl: 36 }, // Decreased for LG
                                    height: { xs: 36, sm: 36, md: 36, lg: 28, xl: 36 }, // Decreased for LG
                                    [COMPACT_HEIGHT_MQ]: { width: 26, height: 26 },
                                  }}
                                />
                              ) : (
                                // Iconsax icon components don't accept MUI's `sx` prop (only
                                // variant/color/size) — a raw sx object here would just get
                                // spread onto the underlying <svg> as an inert DOM attribute
                                // and do nothing. Wrap in a Box carrying the exact same
                                // responsive dimensions the old fontSize-based sizing used,
                                // and let the svg fill it via size="100%" (valid since every
                                // Iconsax icon has a viewBox).
                                <Box
                                  sx={{
                                    width: { xs: 26, sm: 26, md: 26, lg: 20, xl: 26 }, // Decreased for LG
                                    height: { xs: 26, sm: 26, md: 26, lg: 20, xl: 26 },
                                    [COMPACT_HEIGHT_MQ]: { width: 24, height: 24 },
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <item.Icon
                                    size="100%"
                                    color="currentColor"
                                    variant={selectedIndex === globalIndex ? "Bold" : "Linear"}
                                  />
                                </Box>
                              )}
                            </ListItemIcon>
                          </ListItemButton>
                        </Tooltip>
                      </ListItem>
                    </Link>
                  );
                })}
              </List>
            </React.Fragment>
          ))}
        </Box>

        {/* Theme toggle + Profile — moved down from TopNav, pinned to the very end of the rail */}
        <List
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: { xs: 0.35, sm: 0.35, md: 0.35, lg: 0.5, xl: 0.65 },
              mt: "auto",
              pt: 0,
              pb: 1.5,
              [COMPACT_HEIGHT_MQ]: { gap: "3px", pb: 1 },
            }}
          >
            <ListItem disablePadding sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
              <Tooltip title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"} placement="right" arrow componentsProps={RAIL_TOOLTIP_SX}>
                <ListItemButton
                  onClick={toggleTheme}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    borderRadius: "12px",
                    padding: 0,
                    width: { xs: 46, sm: 46, md: 46, lg: 40, xl: 48 },
                    height: { xs: 46, sm: 46, md: 46, lg: 40, xl: 48 },
                    minWidth: { xs: 46, sm: 46, md: 46, lg: 40, xl: 48 },
                    [COMPACT_HEIGHT_MQ]: { width: 40, height: 40, minWidth: 40 },
                    flexGrow: 0,
                    flexShrink: 0,
                    mx: "auto",
                    color: theme.palette.text.primary,
                    transition: "background-color 0.12s ease",
                    "&:hover": { backgroundColor: theme.palette.action.hover },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 0, display: "flex", justifyContent: "center", alignItems: "center", color: theme.palette.text.primary }}>
                    <Box sx={{ width: { xs: 26, sm: 26, md: 26, lg: 20, xl: 26 }, height: { xs: 26, sm: 26, md: 26, lg: 20, xl: 26 }, "@media (max-height:820px)": { width: 24, height: 24 }, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {isDarkMode ? (
                        <LightModeIconIx size="100%" color="currentColor" />
                      ) : (
                        <DarkModeIconIx size="100%" color="currentColor" />
                      )}
                    </Box>
                  </ListItemIcon>
                </ListItemButton>
              </Tooltip>
            </ListItem>

            <ListItem disablePadding sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
              <Tooltip title="Help" placement="right" arrow componentsProps={RAIL_TOOLTIP_SX}>
                {/* Was a dead button, no onClick/href at all (Rahim,
                    2026-09-04: "same link to the help icon"). Same
                    WhatsApp "book a demo" link wired to the AI Agents page's
                    Learn More button, opened in a new tab. */}
                <ListItemButton
                  component="a"
                  href="https://api.whatsapp.com/send/?phone=923334167724&text&type=phone_number&app_absent=0"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    borderRadius: "12px",
                    padding: 0,
                    width: { xs: 46, sm: 46, md: 46, lg: 40, xl: 48 },
                    height: { xs: 46, sm: 46, md: 46, lg: 40, xl: 48 },
                    minWidth: { xs: 46, sm: 46, md: 46, lg: 40, xl: 48 },
                    [COMPACT_HEIGHT_MQ]: { width: 40, height: 40, minWidth: 40 },
                    flexGrow: 0,
                    flexShrink: 0,
                    mx: "auto",
                    color: theme.palette.text.primary,
                    transition: "background-color 0.12s ease",
                    "&:hover": { backgroundColor: theme.palette.action.hover },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 0, display: "flex", justifyContent: "center", alignItems: "center", color: theme.palette.text.primary }}>
                    <Box sx={{ width: { xs: 26, sm: 26, md: 26, lg: 20, xl: 26 }, height: { xs: 26, sm: 26, md: 26, lg: 20, xl: 26 }, "@media (max-height:820px)": { width: 24, height: 24 }, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <HelpIconIx size="100%" color="currentColor" />
                    </Box>
                  </ListItemIcon>
                </ListItemButton>
              </Tooltip>
            </ListItem>

            <ListItem disablePadding sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
              <Tooltip title={userDetails?.name || "Profile"} placement="right" arrow componentsProps={RAIL_TOOLTIP_SX}>
                <ListItemButton
                  onClick={handleProfileMenuClick}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    borderRadius: "12px",
                    padding: 0,
                    width: { xs: 46, sm: 46, md: 46, lg: 40, xl: 48 },
                    height: { xs: 46, sm: 46, md: 46, lg: 40, xl: 48 },
                    minWidth: { xs: 46, sm: 46, md: 46, lg: 40, xl: 48 },
                    [COMPACT_HEIGHT_MQ]: { width: 40, height: 40, minWidth: 40 },
                    flexGrow: 0,
                    flexShrink: 0,
                    mx: "auto",
                    transition: "background-color 0.12s ease",
                    "&:hover": { backgroundColor: theme.palette.action.hover },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 0, display: "flex", justifyContent: "center", alignItems: "center" }}>
                    <Avatar
                      sx={{
                        bgcolor: "#f28b82",
                        width: { xs: 34, sm: 34, md: 34, lg: 30, xl: 36 },
                        height: { xs: 34, sm: 34, md: 34, lg: 30, xl: 36 },
                        fontSize: { xs: "1.15rem", lg: "1.05rem", xl: "1.2rem" },
                        fontWeight: 500,
                        [COMPACT_HEIGHT_MQ]: { width: 30, height: 30, fontSize: "1rem" },
                        color: "#ffffff",
                        fontFamily: "'Instrument Sans', 'Figtree', 'Roboto', sans-serif",
                      }}
                    >
                      {displayName ? displayName[0].toUpperCase() : "U"}
                    </Avatar>
                  </ListItemIcon>
                </ListItemButton>
              </Tooltip>
            </ListItem>
        </List>

        {/* Profile Menu */}
        <Menu
          anchorEl={profileAnchorEl}
          open={isProfileMenuOpen}
          onClose={handleProfileMenuClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "bottom", horizontal: "left" }}
          PaperProps={{
            sx: {
              ...railPopoverPaperSx,
              width: "280px",
              mb: "10px",
              ml: `${profilePopoverOffset}px`,
            },
          }}
        >
          <Box sx={{ pl: 1.25, pr: 2, py: 1.75, display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              sx={{
                bgcolor: "#f28b82",
                color: "#fff",
                width: 36,
                height: 36,
                fontSize: "0.95rem",
                fontWeight: 500,
                fontFamily: "'Instrument Sans', 'Figtree', 'Roboto', sans-serif",
              }}
            >
              {userDetails?.name ? userDetails.name[0].toUpperCase() : "U"}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "0.88rem",
                  color: RAIL_POPUP_TEXT,
                  lineHeight: 1.3,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {displayName || "User"}
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.76rem",
                  fontWeight: 400,
                  color: RAIL_POPUP_SUBTEXT,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {displayEmail || "email@example.com"}
              </Typography>
            </Box>
            {userDetails?.roleName && (
              <Typography
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: 500,
                  color: RAIL_POPUP_SUBTEXT,
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  alignSelf: "flex-start",
                }}
              >
                {userDetails.roleName}
              </Typography>
            )}
          </Box>

          <Box sx={{ p: 0.75, display: "flex", flexDirection: "column", gap: 0.5 }}>
            <MenuItem
              onClick={() => {
                setWorkspaceModalOpen(true);
                handleProfileMenuClose();
              }}
              sx={railPopupItemSx}
            >
              <Box sx={{ width: 20, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <WorkspacesOutlinedIcon sx={{ fontSize: "18px", color: RAIL_POPUP_SUBTEXT }} />
              </Box>
              <Typography sx={{ fontSize: "0.85rem", fontWeight: 400, color: RAIL_POPUP_TEXT }}>Workspace</Typography>
            </MenuItem>

            <MenuItem
              onClick={() => {
                navigate(`/home/${accountid}/settings`);
                handleProfileMenuClose();
              }}
              sx={railPopupItemSx}
            >
              <Box sx={{ width: 20, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <SettingsIcon sx={{ fontSize: "18px", color: RAIL_POPUP_SUBTEXT }} />
              </Box>
              <Typography sx={{ fontSize: "0.85rem", fontWeight: 400, color: RAIL_POPUP_TEXT }}>User Settings</Typography>
            </MenuItem>
          </Box>

          <Divider sx={{ borderColor: RAIL_POPUP_DIVIDER, mx: 0 }} />

          <Box sx={{ p: 0.75 }}>
            <MenuItem onClick={handleLogout} sx={railPopupItemSx}>
              <Box sx={{ width: 20, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <LogoutIcon sx={{ fontSize: "18px", color: RAIL_POPUP_SUBTEXT }} />
              </Box>
              <Typography sx={{ fontSize: "0.85rem", fontWeight: 400, color: RAIL_POPUP_TEXT }}>Sign out</Typography>
            </MenuItem>
          </Box>
        </Menu>

        {/* Restyled 2026-09-12 (Rahim: "update the ui for this use the same type
            of ui we are using overall the inbox page", then "make this type" —
            pointing at AddContact.jsx's "Create New Contact" dialog as the exact
            reference). Was a completely bare default MUI Dialog before (no
            radius, default 1.25rem title, no close icon). Chrome below is a
            direct match of AddContact.jsx's own Dialog/DialogTitle/DialogContent
            styling (same PaperProps, same title row shape + close icon, same
            padding scale) — not a new one-off design, reusing the app's already-
            established "form dialog" look. Row-level styling inside
            WorkspaceSwitcher (hover, tick/pencil icons) is separate, see there. */}
        <Dialog
          open={workspaceModalOpen}
          onClose={() => setWorkspaceModalOpen(false)}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: {
              borderRadius: "16px",
              boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.15)",
              padding: 0,
              backgroundColor: (theme) => theme.palette.background.paper,
            },
          }}
        >
          {/* Corrected 2026-09-12 (Rahim: "not this, use same header as create
              new contact, go see the code for it") — first pass copied
              AddContact.jsx's (Contacts page) header, which is the OLDER,
              oversized 24px/bold(700) style. The actual reference is THIS
              Inbox's own "Create New Contact" dialog (InboxAll.jsx,
              createContactOpen) — already corrected once before, per its own
              comment: "was 24px/bold(700), well past this app's actual heading
              scale". Copied verbatim from there instead, including its
              borderBottom divider and text.secondary close icon. */}
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              padding: { xs: "12px 16px", sm: "14px 18px", md: "16px 20px", lg: "16px 20px", xl: "16px 20px" },
              marginBottom: 0,
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              backgroundColor: (theme) => theme.palette.background.paper,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "0.9375rem", sm: "1rem", md: "1.05rem", lg: "1.05rem", xl: "1.05rem" },
                }}
              >
                Workspace Settings
              </Typography>
              {/* Subtitle (Rahim, 2026-09-12: "add this text under the header
                  text") — was previously the list's own in-content section
                  label; moved up here instead, so the content below starts
                  straight with the rows, no duplicate label. */}
              <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", mt: 0.25 }}>
                Switch Workspace
              </Typography>
            </Box>
            <IconButton
              onClick={() => setWorkspaceModalOpen(false)}
              size="small"
              aria-label="Close"
              sx={{
                color: (theme) => theme.palette.text.secondary,
                "&:hover": { backgroundColor: (theme) => theme.palette.action.hover, color: (theme) => theme.palette.text.primary },
                "& .MuiSvgIcon-root": { fontSize: { xs: "18px", sm: "20px", md: "22px", lg: "22px", xl: "22px" } },
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          {/* No footer Close button (Rahim, 2026-09-12: "no need to use the
              button close cross button is fine") — the header's X is the only
              close affordance now, same as this reference dialog's own
              intent-driven footer (its Cancel/Create&Chat are real actions,
              not a redundant close). DialogContent's own bottom padding is the
              modal's only bottom spacing now. */}
          <DialogContent
            dividers={false}
            sx={{
              px: { xs: 2, sm: 2.5, md: 3, lg: 3, xl: 3 },
              // Explicit pt to match pb (Rahim, 2026-09-12: "reduce the space
              // from the bottom to match the top") — MUI's DialogContent has
              // its own default top padding baked in; only pb was overridden
              // before, leaving the two asymmetric. Both set to the same
              // value now so the gap above the first row and below the last
              // row are identical.
              pt: { xs: 1.5, sm: 1.75, md: 2, lg: 2, xl: 2 },
              pb: { xs: 1.5, sm: 1.75, md: 2, lg: 2, xl: 2 },
              backgroundColor: (theme) => theme.palette.background.paper,
            }}
          >
            <WorkspaceSwitcher showTrigger={false} />
          </DialogContent>
        </Dialog>
      </Drawer>
    </Box>
  );
});

export default LeftNav;